import React, { useMemo, useState } from "react";
import { Check, KeyRound, X } from "lucide-react";
import { useAuth } from "../lib/auth";

const STRENGTH_RULES = [
  { key: "len", label: "At least 10 characters", test: (p) => p.length >= 10 },
  { key: "upper", label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "digit", label: "One digit", test: (p) => /\d/.test(p) },
  { key: "special", label: "One special character (!@#$…)", test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(p) },
];

export default function ChangePasswordDialog({ open, onClose }) {
  const { changePassword } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const strengthOk = useMemo(() => STRENGTH_RULES.every((r) => r.test(next)), [next]);
  const match = next && next === confirm;
  const formOk = current && strengthOk && match && next !== current;

  if (!open) return null;

  const close = () => {
    setCurrent(""); setNext(""); setConfirm("");
    setErr(""); setOk(""); setBusy(false);
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setOk(""); setBusy(true);
    const r = await changePassword({ currentPassword: current, newPassword: next, confirmPassword: confirm });
    setBusy(false);
    if (r.ok) {
      setOk(r.message || "Password updated.");
      setCurrent(""); setNext(""); setConfirm("");
      setTimeout(close, 1200);
    } else {
      setErr(r.error || "Could not update password.");
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="change-password-dialog"
      style={{
        position: "fixed", inset: 0, zIndex: 80,
        background: "rgba(10,14,26,0.72)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="pf-card pf-grain" style={{ width: 460, padding: 28 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div>
            <div className="pf-overline" style={{ marginBottom: 4 }}>ACCOUNT · SECURITY</div>
            <h2 className="pf-heading" style={{ fontSize: 22, margin: 0 }}>Change password</h2>
          </div>
          <button className="btn btn-ghost" onClick={close} data-testid="change-password-close" aria-label="Close">
            <X size={14} />
          </button>
        </div>

        <form onSubmit={submit}>
          <label className="pf-label" htmlFor="cp-current">CURRENT PASSWORD</label>
          <input id="cp-current" className="pf-input" type="password" autoComplete="current-password" required
            value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="••••••••"
            data-testid="change-password-current" />

          <div style={{ height: 12 }} />

          <label className="pf-label" htmlFor="cp-new">NEW PASSWORD</label>
          <input id="cp-new" className="pf-input" type="password" autoComplete="new-password" required
            value={next} onChange={(e) => setNext(e.target.value)} placeholder="••••••••"
            data-testid="change-password-new" />
          {next && (
            <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px" }} data-testid="change-password-strength">
              {STRENGTH_RULES.map((r) => {
                const okRule = r.test(next);
                return (
                  <li key={r.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontFamily: "'JetBrains Mono', monospace", color: okRule ? "var(--pf-ok)" : "var(--pf-text-dim)" }} data-ok={okRule}>
                    {okRule ? <Check size={12} /> : <X size={12} />} {r.label}
                  </li>
                );
              })}
            </ul>
          )}

          <div style={{ height: 12 }} />

          <label className="pf-label" htmlFor="cp-confirm">CONFIRM NEW PASSWORD</label>
          <input id="cp-confirm" className="pf-input" type="password" autoComplete="new-password" required
            value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••"
            data-testid="change-password-confirm" />
          {confirm && !match && (
            <div style={{ marginTop: 6, fontSize: 11.5, color: "var(--pf-err)", fontFamily: "'JetBrains Mono', monospace" }} data-testid="change-password-mismatch">
              Passwords do not match.
            </div>
          )}
          {next && current && next === current && (
            <div style={{ marginTop: 6, fontSize: 11.5, color: "var(--pf-err)", fontFamily: "'JetBrains Mono', monospace" }} data-testid="change-password-same">
              New password must differ from the current one.
            </div>
          )}

          {err && (
            <div className="pf-sev pf-sev-error" style={{ display: "block", marginTop: 14, padding: "8px 10px" }} data-testid="change-password-error">
              {err}
            </div>
          )}
          {ok && (
            <div className="pf-sev pf-sev-info" style={{ display: "block", marginTop: 14, padding: "8px 10px", color: "var(--pf-ok)" }} data-testid="change-password-success">
              {ok}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
            <button type="button" className="btn btn-ghost" onClick={close} disabled={busy} data-testid="change-password-cancel" style={{ flex: 1 }}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy || !formOk} data-testid="change-password-submit" style={{ flex: 1 }}>
              {busy ? <><span className="pf-spin" /> Updating…</> : <><KeyRound size={14} /> Update password</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
